import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from '@restworld/utility-types';
import { EmployerJPState, getEmployerJPState } from '@restworld/utils-common';

import {
  EmployerCard,
  EmployerCardHeader,
  EmployerCardFooter,
  EmployerCardInfo,
  EmployerBadge,
  EmployerCandidate,
} from '@restworld/ui-components';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { TFunction } from 'i18next';

export function badgeVariant({ jpState }: { jpState: EmployerJPState }) {
  const variant = 'neutral';
  switch (jpState) {
    case 'draft':
      return 'warning';
    case 'open':
      return 'success';
    case 'offline':
      return 'neutral';
    case 'closed':
      return 'disabled';
    default:
      return variant;
  }
}

export function bagdeText({
  jpState,
  lastStatusChangeDate,
  t,
}: {
  jpState: EmployerJPState;
  t: TFunction;
  lastStatusChangeDate?: string;
}) {
  switch (jpState) {
    case 'draft':
      return t('draft');
    case 'pending_approval':
      return t('pending_approval');
    case 'open':
      return t('open');
    case 'offline':
      return `${t('offline')} ${t('since')} ${
        lastStatusChangeDate ? format(new Date(lastStatusChangeDate), 'dd/MM/yyyy', { locale: it }) : ''
      }`;
    default:
      return `${t('closed')} ${t('since')} ${
        lastStatusChangeDate ? format(new Date(lastStatusChangeDate), 'dd/MM/yyyy', { locale: it }) : ''
      }`;
  }
}

export interface EmployerJobListingCardProps {
  jpId?: string;
  candidates?: {
    count: number;
    candidates: EmployerCandidate[];
    message: string;
    isAllSpontaneous?: boolean;
  };
  href: string;
  jobTitle: string;
  lastStatusChangeDate?: string;
  labels: { visits: number; candidates: number; qualified: number };
  occupationLabel: string;
  restaurant: {
    logo: string;
    address: string;
    name: string;
  };
  staffRequested: number;
  status: Status;
  domainInterestKey: string;
  isUsingPPU?: boolean;
  onClick?: () => void;
  onRequestChanges?: () => void;
  onDuplicate?: (jpId: string) => void;
  onDeleteDraft?: (jpId: string) => void;
  onPutOffline?: (jpId: string) => void;
  onPutOnline?: (jpId: string) => void;
}

export const EmployerJobListingCard = memo(function EmployerJobListingCard({
  jpId,
  status,
  staffRequested,
  labels,
  occupationLabel,
  jobTitle,
  restaurant,
  href,
  candidates,
  domainInterestKey,
  isUsingPPU,
  onClick,
  onRequestChanges,
  onDuplicate,
  onDeleteDraft,
  onPutOffline,
  onPutOnline,
  lastStatusChangeDate,
}: EmployerJobListingCardProps) {
  const { t } = useTranslation('job-positions-page');

  const jpState = useMemo(() => getEmployerJPState(status?.key), [status?.key]);

  const labelVariant = useMemo(() => (jpState === 'open' ? 'info' : 'disabled'), [jpState]);

  const currentLabels: EmployerBadge[] = useMemo(
    () =>
      labels
        ? [
            { text: `${labels.visits || 0} ${t('visits')}`, variant: labelVariant },
            { text: `${labels.candidates || 0} ${t('candidates')}`, variant: labelVariant },
            { text: `${labels.qualified || 0} ${t('qualified')}`, variant: labelVariant },
          ]
        : [],
    [labelVariant, labels, t]
  );

  const info = useMemo(
    () => ({
      address: restaurant.address,
      businessName: restaurant.name,
      occupation: occupationLabel,
      openPositions: staffRequested,
      domainInterestKey,
    }),
    [domainInterestKey, occupationLabel, restaurant.address, restaurant.name, staffRequested]
  );

  const notification = useMemo(
    () => ({
      candidateCount: candidates?.count,
      message: candidates?.message,
      candidates: candidates?.candidates,
      isAllSpontaneous: candidates?.isAllSpontaneous,
    }),
    [candidates?.candidates, candidates?.count, candidates?.isAllSpontaneous, candidates?.message]
  );
  return (
    <EmployerCard href={href} onClick={onClick}>
      <EmployerCardHeader
        logo={restaurant.logo}
        title={jobTitle}
        badge={{ text: bagdeText({ jpState, t, lastStatusChangeDate }), variant: badgeVariant({ jpState }) }}
        statusKey={status?.key}
        jpId={jpId}
        occupationLabel={occupationLabel}
        isUsingPPU={isUsingPPU}
        onRequestChanges={onRequestChanges}
        onDuplicate={onDuplicate}
        onDeleteDraft={onDeleteDraft}
        onPutOffline={onPutOffline}
        onPutOnline={onPutOnline}
      />
      <EmployerCardInfo info={info} />
      <EmployerCardFooter labels={currentLabels} notification={notification} />
    </EmployerCard>
  );
});

export default memo(EmployerJobListingCard);
